import React from 'react';
import TimeSelect from './TimeSelect';
import './DateTimeField.css';

/**
 * Format-aware datetime field — a drop-in replacement for `<input type="datetime-local">`.
 *
 * Native datetime-local renders the time in the OS locale (always 24h here), which
 * clashes with the business's chosen time format. This splits it into a native date
 * input (date isn't the problem) + the shared {@link TimeSelect} — the SAME searchable
 * hour picker used in team working hours and Settings → Calendar — so every time control
 * across the app looks identical. The value stays canonical "YYYY-MM-DDTHH:mm".
 */
const pad = (n: number) => String(n).padStart(2, '0');

const todayYmd = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

interface Props {
    /** "YYYY-MM-DDTHH:mm" or "" */
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    /** Optional min for the date part ("YYYY-MM-DD"). */
    minDate?: string;
    /** Minutes between proposed times (the business "booking increment"). Default 30. */
    stepMinutes?: number;
    className?: string;
    'data-cy'?: string;
}

export default function DateTimeField({ value, onChange, disabled, minDate, stepMinutes, className, ...rest }: Props) {
    const hasValue = !!value && value.length >= 16;
    const datePart = hasValue ? value.slice(0, 10) : '';
    const timePart = hasValue ? value.slice(11, 16) : '';

    const setDate = (d: string) => onChange(d ? `${d}T${timePart || '09:00'}` : '');
    const setTime = (t: string) => onChange(t ? `${datePart || todayYmd()}T${t}` : '');

    return (
        <div className={`gs-dtf ${className ?? ''}`}>
            <input
                type="date"
                className="form-control form-control-sm gs-dtf__date"
                value={datePart}
                min={minDate}
                disabled={disabled}
                onChange={(e) => setDate(e.target.value)}
            />
            <div className="gs-dtf__time">
                <TimeSelect
                    value={timePart}
                    onChange={setTime}
                    disabled={disabled}
                    allowCustom
                    stepMinutes={stepMinutes}
                    data-cy={rest['data-cy']}
                />
            </div>
        </div>
    );
}
